import { action } from '@ember/object';
import { cancel, later } from '@ember/runloop';
import type { EmberRunTimer } from '@ember/runloop/types';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import Ember from 'ember';
import { task } from 'ember-concurrency';
import type { LatLngObject } from 'garaje/helpers/resource-coords';
import toLatLngArray, { toLatLngObject } from 'garaje/helpers/resource-coords';
import type { Resource } from 'garaje/types/maps/live-map/LiveMapServiceV2';
import type { MapIconLabelOptions, MapIconOptions } from 'garaje/utils/map/build-map-icons';
import type { NeighborhoodPolygons } from 'garaje/utils/neighborhood-polygons';
import type { DivIcon, LatLng, LeafletEvent, LeafletMouseEvent, Map, Polygon } from 'leaflet';
import leaflet from 'leaflet';
import { localCopy } from 'tracked-toolbox';

export const POLYGON_BASE_OPTIONS = {
  weight: 4,
  fillOpacity: 0.6,
  opacity: 1,
};

export const POLYGON_COLORS = {
  disabled: {
    color: '#C9CBD4', // carbon-20
    fillColor: '#EBEBF0', // carbon-10
  },
  enabled: {
    color: '#4EC176', // cilantro-30
    fillColor: '#C7F5D2', // cilantro-10
  },
  unavailable: {
    color: '#FA4338', // red-40
    fillColor: '#FA433840', // red-40 (transparent)
  },
};
export interface ResourceMapPointPolygon {
  name: string;
  icon: DivIcon;
  polygon: Polygon;
}

export interface ResourceMapPoint {
  resource: Resource;
  icon: DivIcon;
  tooltipLabel: string;
  coordinates: [number, number];
}

export interface ResourceIconConfig {
  [resourceType: string]: {
    iconOptions: MapIconOptions;
    labelOptions?: MapIconLabelOptions;
    onClickHandler?: () => void;
  };
}

interface BaseMapComponentArgs {
  mapWidth: number;
  mapHeight: number;
  containerHeight: number;
  rasterImageUrl: string;
  shouldSetMaxBounds: boolean;
  leafletMap: Map;
  resources: Resource[];
  resourceMapPoints: ResourceMapPoint[];
  resourceMapPointPolygons: ResourceMapPointPolygon[];
  resourceMapPolygons: NeighborhoodPolygons;
  resourceConfig: ResourceIconConfig;
  getNeighborhoodColor: (neighborhoodId: string) => string | undefined;
  setLeafletMap: (map: Map | null) => void;
  shareMapDimensions: (dimensions: [number, number]) => void;
}

export default class BaseMapComponent extends Component<BaseMapComponentArgs> {
  crs = leaflet.CRS.Simple;

  @localCopy('args.mapWidth', 1200) declare mapWidth: number;
  @localCopy('args.mapHeight', 800) declare mapHeight: number;
  @tracked declare map: Map;
  @tracked isReady = false;
  @tracked imageLoaded = false;
  @tracked hasOpenPopup = false;

  @tracked containerWidth = 800;
  @tracked containerHeight = this.getContainerHeight();
  @tracked zoomDelta = 0.5;
  @tracked declare originalZoomValue: number;

  initialZoom = 0;
  initialCenter = [0, 0];

  @tracked imageBounds = null;
  @tracked isInteraction = false;
  @tracked debouncedInteractionOff: EmberRunTimer | undefined;
  @tracked cursorX: number | null | undefined;
  @tracked cursorY: number | null | undefined;

  getContainerHeight(): number {
    if (this.args.containerHeight) {
      return this.args.containerHeight;
    }

    const wrapper = document.querySelector('#resource-map-wrapper');
    const clientHeight = wrapper instanceof HTMLElement ? wrapper.clientHeight : 0;
    return clientHeight;
  }

  get isLandscape(): boolean {
    return this.mapWidth > this.mapHeight;
  }

  get mapDimensions(): [number, number] {
    return [this.mapWidth, this.mapHeight];
  }

  // Exactly match the bounds calculation from ResourceMap
  get mapBounds(): leaflet.LatLngBounds {
    const { mapWidth, mapHeight } = this;
    const southWestPoint = new leaflet.Point(0, 0);
    // this was originally:
    // const northEastPoint = new leaflet.Point(mapWidth, -mapHeight);
    const northEastPoint = new leaflet.Point(mapWidth, mapHeight);
    return new leaflet.LatLngBounds(
      leaflet.CRS.Simple.pointToLatLng(southWestPoint, 0),
      leaflet.CRS.Simple.pointToLatLng(northEastPoint, 0),
    );
  }

  get maxZoom(): number {
    const delta = 2.15;
    if (this.isLandscape) {
      return Math.sqrt(this.containerHeight / this.mapHeight) * delta;
    }
    return Math.sqrt(this.containerWidth / this.mapWidth) * delta;
  }

  get minZoom(): number {
    const delta = 1.35; // This delta prevents images from getting cut off
    if (this.isLandscape) {
      return -Math.sqrt(this.mapWidth / this.containerWidth) * delta;
    }
    return -Math.sqrt(this.mapHeight / this.containerHeight) * delta;
  }

  get enableAnimation(): boolean {
    return !Ember.testing;
  }

  get zoomPanOptions(): { animate: boolean } {
    return {
      animate: this.enableAnimation,
    };
  }

  get shouldShowTooltips(): boolean {
    return !this.hasOpenPopup;
  }

  getPolygonCenter(polygon: Polygon): LatLng {
    return polygon.getBounds().getCenter();
  }

  getLocation = (coordinates: [number, number]): number[] | undefined => {
    return toLatLngArray(coordinates, [this.mapWidth, this.mapHeight]);
  };

  @action
  convertToLatLngArray(coordinates: Array<[number, number]>): LatLngObject[] {
    const coords = coordinates.map((coordsArray) => toLatLngObject(coordsArray, [this.mapWidth, this.mapHeight]));
    return coords;
  }

  @action
  async onInsert(): Promise<void> {
    this.updateContainerDimensions();
    await this.loadImageTask.perform();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  }

  @action
  onDestroy(): void {
    this.args.setLeafletMap(null);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    window.removeEventListener('resize', this.handleResize);
  }

  @action
  updateContainerDimensions(): void {
    const wrapper = document.querySelector('#resource-map-wrapper');
    if (wrapper instanceof HTMLElement) {
      const { clientWidth, clientHeight } = wrapper;
      this.containerWidth = Math.max(clientWidth, 400);
      this.containerHeight = Math.max(clientHeight, 400);
    } else {
      // eslint-disable-next-line no-console
      console.warn('Could not find resource-map-wrapper element');
      this.containerWidth = 800;
      this.containerHeight = 600;
    }
  }

  @action
  handleResize(): void {
    this.updateContainerDimensions();
    if (this.args.leafletMap) {
      this.args.leafletMap.invalidateSize();
    }
  }

  @action
  onFloorUpdate(): void {
    this.isReady = false;
    this.hasOpenPopup = false;
    if (this.args.leafletMap) {
      this.args.leafletMap.eachLayer((layer) => {
        // layer.options.isArea does not exist in the API
        if (layer.options && (layer.options as { isArea?: boolean }).isArea) {
          this.args.leafletMap.removeLayer(layer);
        }
      });
    }
    void this.loadImageTask.perform();
  }

  @action
  onLoadImage(): void {
    // Without the timeout, when image is cached LeafletMap will render the markers before the image is fit to the bounds
    // When the image is fit the markers will be out of place. The timeout gives time for the image to be fit after loading
    this.imageLoaded = true;
    later(
      this,
      () => {
        if (!Ember.testing && this.args.leafletMap) {
          this.args.leafletMap.invalidateSize();
          this.args.leafletMap.fitBounds(this.mapBounds, { padding: [1, 1] });
          this.originalZoomValue = this.args.leafletMap.getZoom();
        }
        this.isReady = true;
      },
      500,
    );
  }

  @action
  registerMap(event: LeafletEvent): void {
    const { target: map } = event as { target: Map };

    // map._onPanTransitionEnd is not part of the map API?
    // if (Ember.testing) {
    //   map._onPanTransitionEnd = () => {
    //     map.fire('moveend');
    //   };
    // }

    map.on('moveend', () => {
      map.setMaxBounds(this.args.shouldSetMaxBounds ? this.mapBounds : []);
    });

    this.args.setLeafletMap(map);

    later(
      this,
      () => {
        map.invalidateSize();
      },
      100,
    );
  }

  @action
  handleMouseMove(ev: LeafletMouseEvent): void {
    const { offsetX, offsetY } = ev.originalEvent;
    if (!this.isInteraction) {
      this.cursorX = offsetX;
      this.cursorY = offsetY;
    }
  }

  @action
  resetCursor(): void {
    this.cursorX = null;
    this.cursorY = null;
  }

  @action
  toggleInteractionOn(): void {
    cancel(this.debouncedInteractionOff);
    this.isInteraction = true;
  }

  @action
  toggleInteractionOff(): void {
    this.isInteraction = false;
    this.resetCursor();
  }

  @action
  onPopupOpen(): void {
    this.hasOpenPopup = true;
    this.toggleTooltipPane(false);
  }

  @action
  onPopupClose(): void {
    this.hasOpenPopup = false;
    this.toggleTooltipPane(true);
  }

  toggleTooltipPane(show: boolean): void {
    const pane = this.args.leafletMap?.getPanes()?.tooltipPane;
    if (pane) {
      pane.style.display = show ? '' : 'none';
    }
  }

  loadImageTask = task({ drop: true }, async () => {
    this.isReady = false;
    this.imageLoaded = false;

    return new Promise<void>((resolve, reject) => {
      const image = new Image();
      image.src = this.args.rasterImageUrl;

      image.onerror = (err) => {
        // eslint-disable-next-line no-console
        console.error('Error loading map image:', err);
        reject(new Error('Failed to load map image'));
      };

      image.onload = (ev) => {
        const imgElement = ev.target as HTMLImageElement;
        this.mapHeight = imgElement.height;
        this.mapWidth = imgElement.width;
        // Pass up the map dimensions to the parent component
        this.args.shareMapDimensions([imgElement.width, imgElement.height]);

        this.imageLoaded = true;
        resolve();
      };
    });
  });
}
